import axios from "axios";

class SnowflakeService {
  constructor() {
    // Backend API URL - this should match your backend server
    // In production (Vercel), use relative URLs. In development, use localhost
    const isProduction = process.env.NODE_ENV === 'production';
    const isNotLocalhost = typeof window !== 'undefined' && window.location.hostname !== 'localhost';

    if (isProduction || isNotLocalhost) {
      this.apiBaseURL = "/api";
    } else {
      this.apiBaseURL = process.env.VUE_APP_API_BASE_URL || "http://localhost:3001/api";
    }

    console.log(`🔗 Environment Detection:`);
    console.log(`   NODE_ENV: ${process.env.NODE_ENV}`);
    console.log(`   hostname: ${typeof window !== 'undefined' ? window.location.hostname : 'server-side'}`);
    console.log(`   isProduction: ${isProduction}`);
    console.log(`   isNotLocalhost: ${isNotLocalhost}`);
    console.log(`   Selected API Base URL: ${this.apiBaseURL}`);
  }

  // Fetch real GMV data from Snowflake via backend API
  async getGMVData() {
    try {
      const fullUrl = `${this.apiBaseURL}/gmv-data`;
      console.log(`📡 Making API call to: ${fullUrl}`);
      console.log(`🌐 Backend type: ${this.apiBaseURL.includes('localhost') ? '🏠 Local Express Server (port 3001)' : '☁️ Vercel Serverless Functions'}`);
      console.log(`🔍 Environment: ${process.env.NODE_ENV || 'development'}`);

      // Try to fetch from the backend API with cache-busting
      const response = await axios.get(fullUrl, {
        timeout: 30_000, // Increased timeout to 30 seconds
        params: {
          _t: Date.now(), // Cache-busting timestamp
          _r: Math.random().toString(36).substring(7) // Random string
        },
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      console.log(`📊 API Response Status: ${response.status}`);
      console.log(`📋 API Response Data:`, response.data);

      if (response.data.success && response.data.data) {
        console.log(
          `✅ Successfully received ${response.data.data.length} records from Snowflake backend`
        );
        return {
          success: true,
          data: response.data.data,
          isSimulated: false,
        };
      } else if (response.data.error) {
        // Backend responded but with an error
        const errorMessage = response.data.error || "Unknown backend error";
        console.error("❌ Backend returned error:", errorMessage);
        return {
          success: false,
          error: errorMessage,
          details: response.data.details || "No additional details available",
        };
      } else {
        // Unexpected response format
        console.error("❌ Unexpected response format:", response.data);
        return {
          success: false,
          error: "Unexpected response format from backend",
          details: "Backend returned data in an unexpected format",
        };
      }
    } catch (error) {
      console.error("❌ Failed to connect to backend:", error.message);

      let errorMessage = "Failed to connect to Snowflake backend";
      let details = error.message;

      if (error.code === "ECONNREFUSED") {
        errorMessage = "Backend server not running";
        details = `Cannot connect to ${this.apiBaseURL}. Make sure the backend server is running on port 3001.`;
      } else if (error.code === "ENOTFOUND") {
        errorMessage = "Backend server not found";
        details = `Cannot resolve ${this.apiBaseURL}. Check your network connection.`;
      } else if (error.response) {
        // Backend responded with an error
        errorMessage = `Backend error (${error.response.status})`;
        details =
          error.response.data?.message ||
          error.response.data?.error ||
          error.response.statusText;
      }

      return {
        success: false,
        error: errorMessage,
        details: details,
      };
    }
  }

  // Method to get the latest GMV number
  async getLatestGMV() {
    const result = await this.getGMVData();
    if (result.success && result.data.length > 0) {
      return result.data[0].TOTAL_GMV_LIFETIME;
    }
    return 0;
  }

  // Method to format currency
  formatCurrency(amount) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  // Health check method to verify backend connectivity
  async checkBackendHealth() {
    try {
      const response = await axios.get(`${this.apiBaseURL}/health`, {
        timeout: 5000,
      });
      return response.status === 200;
    } catch {
      return false;
    }
  }
}

export default new SnowflakeService();
